import React, { ChangeEvent, useRef, useEffect, useCallback } from "react";
import Image from "next/image";
import styles from "./UploadFile.module.scss";
import { Toast } from "@douyinfe/semi-ui";
import { v4 as uuidv4 } from "uuid";
import { useFileUpload } from "@/app/hooks/useFileUpload";
import { fileUploadApi } from "@/app/api/file";
import { getPublicImagePath } from "@/app/utils/image";
import Locale from "@/app/locales";
import { UploadFileInfo } from "@/app/store/types";
import { ReportScene, reportScene } from "@/app/services/DataReporter";

export default function UploadFile({ disabled }: { disabled: boolean }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const filePickerOpenRef = useRef(false);
  const fileProcessedRef = useRef(false);
  const { onSuccess, beforeUpload, onError } = useFileUpload(fileInputRef);

  const processFile = useCallback(
    async (file: File) => {
      reportScene(
        ReportScene.UPLOAD_FILE,
        JSON.stringify({
          status: "process file start",
        }),
      );
      if (fileProcessedRef.current) return;
      fileProcessedRef.current = true;
      filePickerOpenRef.current = false;

      const fileId = uuidv4();
      const maxSize = 100;
      if (file.size > maxSize * 1024 * 1024) {
        Toast.warning(
          Locale.chatInput.fileLimitTips.replace("${0}", `${maxSize}`),
        );
        return;
      }
      reportScene(
        ReportScene.UPLOAD_FILE,
        JSON.stringify({
          status: "check file size start",
        }),
      );
      await beforeUpload(file, fileId);

      fileUploadApi({
        fileId,
        file,
        onError: (uid: string, fileInfo: UploadFileInfo) => {
          onError(uid, fileInfo);
        },
        onSuccess,
      });
    },
    [beforeUpload, onError, onSuccess],
  );

  const checkFileInput = useCallback(() => {
    if (!filePickerOpenRef.current || fileProcessedRef.current) return;
    const input = fileInputRef.current;
    if (input?.files && input.files.length > 0) {
      processFile(input.files[0]);
    }
  }, [processFile]);

  const handleIconClick = () => {
    reportScene(
      ReportScene.UPLOAD_FILE,
      JSON.stringify({
        status: "icon click start",
      }),
    );
    if (disabled) return;
    filePickerOpenRef.current = true;
    fileProcessedRef.current = false;
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    reportScene(
      ReportScene.UPLOAD_FILE,
      JSON.stringify({
        status: "file change start",
      }),
    );
    if (!e.target.files || e.target.files.length === 0) return;
    processFile(e.target.files[0]);
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setTimeout(checkFileInput, 500);
      }
    };

    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        setTimeout(checkFileInput, 500);
      }
    };

    const handleFocus = () => {
      setTimeout(checkFileInput, 800);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("focus", handleFocus);
    };
  }, [checkFileInput]);

  return (
    <div
      className={`${styles.uploadContainer} ${disabled ? styles.disabled : ""}`}
    >
      <input
        type="file"
        onChange={handleFileChange}
        className={styles.uploadIpt}
        ref={fileInputRef}
      />
      <button className={styles.operationBtn}>
        <Image
          src={getPublicImagePath("file_send.png")}
          alt="File Send"
          width={20}
          height={20}
          unoptimized
          onClick={handleIconClick}
        />
      </button>
    </div>
  );
}
